import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';
import { Languages, Loader2 } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { translateText } from '../../services/api';

const MultilingualInput = ({ 
  type = 'input', // 'input' or 'textarea'
  name,
  label,
  valuePT,
  valueEN,
  onChangePT,
  onChangeEN,
  placeholder,
  rows = 4,
  required = false,
  className = ''
}) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [translating, setTranslating] = useState(false);
  const [activeTab, setActiveTab] = useState('pt');

  const handleAutoTranslate = async () => {
    // Determine source and target based on which field has content
    let sourceText, sourceLang, targetLang, setTargetValue;
    
    if (valuePT && !valueEN) {
      sourceText = valuePT;
      sourceLang = 'pt';
      targetLang = 'en';
      setTargetValue = onChangeEN;
    } else if (valueEN && !valuePT) {
      sourceText = valueEN;
      sourceLang = 'en';
      targetLang = 'pt';
      setTargetValue = onChangePT;
    } else if (activeTab === 'pt' && valuePT) {
      sourceText = valuePT;
      sourceLang = 'pt';
      targetLang = 'en';
      setTargetValue = onChangeEN;
    } else if (activeTab === 'en' && valueEN) {
      sourceText = valueEN;
      sourceLang = 'en';
      targetLang = 'pt';
      setTargetValue = onChangePT;
    } else {
      toast({
        title: t('common.error'),
        description: 'Por favor, preencha pelo menos um campo antes de traduzir',
        variant: 'destructive',
      });
      return;
    }

    setTranslating(true);

    try {
      const result = await translateText(sourceText, sourceLang, targetLang);
      
      if (result.success) {
        setTargetValue({ target: { value: result.data.translated_text } });
        toast({
          title: t('common.success'),
          description: `Traduzido de ${sourceLang.toUpperCase()} para ${targetLang.toUpperCase()}`,
        });
      } else {
        toast({
          title: t('common.error'),
          description: result.error || 'Erro na tradução',
          variant: 'destructive',
        });
      }
    } catch (error) {
      toast({
        title: t('common.error'),
        description: 'Erro ao traduzir texto',
        variant: 'destructive',
      });
    } finally {
      setTranslating(false);
    }
  };

  const InputComponent = type === 'textarea' ? Textarea : Input;

  return (
    <div className={`space-y-2 ${className}`}>
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-[#2B2D42]">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleAutoTranslate}
          disabled={translating}
          className="text-[#00B4D8] hover:text-[#48CAE4]"
        >
          {translating ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Traduzindo...
            </>
          ) : (
            <>
              <Languages className="mr-2 h-4 w-4" />
              Traduzir automaticamente
            </>
          )}
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="pt" className="flex items-center gap-2">
            🇵🇹 Português
          </TabsTrigger>
          <TabsTrigger value="en" className="flex items-center gap-2">
            🇬🇧 English
          </TabsTrigger>
        </TabsList>

        <TabsContent value="pt" className="mt-2">
          <InputComponent
            value={valuePT}
            onChange={onChangePT}
            placeholder={placeholder?.pt || `${label} em Português`}
            required={required}
            rows={type === 'textarea' ? rows : undefined}
            className="w-full"
          />
        </TabsContent>

        <TabsContent value="en" className="mt-2">
          <InputComponent
            value={valueEN}
            onChange={onChangeEN}
            placeholder={placeholder?.en || `${label} in English`}
            required={required}
            rows={type === 'textarea' ? rows : undefined}
            className="w-full"
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default MultilingualInput;
